import { AuthProvider, UserIdentity } from 'react-admin';
import { AdminRole, AdminRoleLevel, RolePermissions, AdminPermission } from '../types/admin.types';

/**
 * EKAA Admin Auth Provider
 * Uses Keycloak via API proxy for authentication
 */

// Config
const API_URL = import.meta.env.VITE_API_URL || 'https://api.e-kaa.app';
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'ekaa';
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'ekaa-admin';

// Storage keys (must match across the app)
const TOKEN_KEY = 'ekaa_admin_token';
const REFRESH_TOKEN_KEY = 'ekaa_admin_refresh_token';
const USER_KEY = 'ekaa_admin_user';

// Types
// Exportée (#1088) : c'est la forme que useAuth() (App.tsx) expose comme
// `user` — plus riche que UserIdentity de react-admin (id/fullName/avatar
// seulement), nécessaire aux consommateurs existants (email, firstName,
// lastName, roles bruts affichés dans SettingsPage.tsx notamment).
export interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * Parse JWT token payload
 */
function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if token is expired
 */
function isTokenExpired(token: string): boolean {
  const parsed = parseJwt(token);
  if (!parsed || !parsed.exp) return true;
  return parsed.exp * 1000 < Date.now();
}

/**
 * Clear all auth data
 */
function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Get stored user info
 * Exportée (#1088) — useAuth() (App.tsx) la lit directement plutôt que de
 * dupliquer la logique localStorage/parsing.
 */
export function getStoredUser(): UserInfo | null {
  const stored = localStorage.getItem(USER_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Handle token response from Keycloak
 */
function handleTokenResponse(data: TokenResponse): UserInfo {
  const parsed = parseJwt(data.access_token);

  const userInfo: UserInfo = {
    id: parsed.sub || '',
    email: parsed.email || '',
    firstName: parsed.given_name || '',
    lastName: parsed.family_name || '',
    roles: parsed.realm_access?.roles || [],
  };

  localStorage.setItem(TOKEN_KEY, data.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(userInfo));

  return userInfo;
}

/**
 * Refresh access token
 */
async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refreshToken,
        realm: KEYCLOAK_REALM,
        clientId: KEYCLOAK_CLIENT_ID,
      }),
    });

    if (response.ok) {
      const data: TokenResponse = await response.json();
      handleTokenResponse(data);
      return true;
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
  }

  clearAuth();
  return false;
}

/**
 * EKAA Admin Auth Provider
 */
export const authProvider: AuthProvider = {
  /**
   * Login with email and password via Keycloak proxy
   */
  login: async ({ username, password }: { username: string; password: string }) => {
    try {
      const response = await fetch(`${API_URL}/auth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          realm: KEYCLOAK_REALM,
          clientId: KEYCLOAK_CLIENT_ID,
        }),
      });

      if (response.ok) {
        const data: TokenResponse = await response.json();
        handleTokenResponse(data);
        return Promise.resolve();
      }

      const errorData = await response.json().catch(() => ({}));

      if (errorData.error === 'invalid_grant') {
        throw new Error('Email ou mot de passe incorrect');
      }
      if (errorData.error === 'unauthorized_client') {
        throw new Error("Client non autorisé. Contactez l'administrateur.");
      }

      throw new Error(errorData.message || 'Erreur de connexion');
    } catch (error: any) {
      clearAuth();
      throw new Error(error.message || 'Erreur de connexion au serveur');
    }
  },

  /**
   * Logout and clear session
   */
  logout: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (refreshToken) {
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            refreshToken,
            realm: KEYCLOAK_REALM,
            clientId: KEYCLOAK_CLIENT_ID,
          }),
        });
      } catch {
        // Ignore logout errors
      }
    }

    clearAuth();
    return Promise.resolve();
  },

  /**
   * Check if user is authenticated
   */
  checkAuth: async () => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      return Promise.reject({ redirectTo: '/login' });
    }

    // Check if token is expired
    if (isTokenExpired(token)) {
      // Try to refresh
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        return Promise.reject({ redirectTo: '/login' });
      }
    }

    return Promise.resolve();
  },

  /**
   * Handle API errors
   */
  checkError: async (error: any) => {
    const status = error?.status || error?.response?.status;

    if (status === 401 || status === 403) {
      // Try to refresh token first
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        clearAuth();
        return Promise.reject({ redirectTo: '/login' });
      }
      return Promise.resolve();
    }

    // Check for GraphQL auth errors
    if (error?.graphQLErrors) {
      const authError = error.graphQLErrors.find(
        (e: any) =>
          e.extensions?.code === 'UNAUTHENTICATED' ||
          e.extensions?.code === 'FORBIDDEN'
      );

      if (authError) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          clearAuth();
          return Promise.reject({ redirectTo: '/login' });
        }
        return Promise.resolve();
      }
    }

    return Promise.resolve();
  },

  /**
   * Get current user identity
   */
  getIdentity: async (): Promise<UserIdentity> => {
    const user = getStoredUser();

    if (user) {
      return {
        id: user.id,
        fullName: `${user.firstName} ${user.lastName}`.trim() || user.email,
        avatar: undefined, // Could fetch from profile API if needed
      };
    }

    // Try to get from token
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      const parsed = parseJwt(token);
      if (parsed) {
        return {
          id: parsed.sub,
          fullName: `${parsed.given_name || ''} ${parsed.family_name || ''}`.trim() || parsed.email,
          avatar: undefined,
        };
      }
    }

    return {
      id: 'unknown',
      fullName: 'Admin',
    };
  },

  /**
   * Get user permissions (converted from Keycloak roles)
   */
  getPermissions: async (): Promise<AdminPermission[]> => {
    const user = getStoredUser();
    if (!user?.roles) return [];

    // Convert Keycloak roles to granular permissions
    const permissions = new Set<AdminPermission>();

    for (const role of user.roles) {
      // Check if role is a valid AdminRole
      const adminRole = role.toUpperCase() as AdminRole;
      if (RolePermissions[adminRole]) {
        RolePermissions[adminRole].forEach((p) => permissions.add(p));
      }
    }

    // If user has any admin-like role, give at least PLANS_READ for now
    if (
      user.roles.some((r) =>
        ['admin', 'ADMIN', 'super_admin', 'SUPER_ADMIN', 'ekaa-admin', 'ekaa-super-admin'].includes(r)
      )
    ) {
      // Add basic read permissions if not already present
      permissions.add(AdminPermission.USERS_READ);
      permissions.add(AdminPermission.CARDS_READ);
      permissions.add(AdminPermission.PLANS_READ);
      permissions.add(AdminPermission.SUBSCRIPTIONS_READ);
      permissions.add(AdminPermission.FINANCE_READ);
      permissions.add(AdminPermission.CONFIG_READ);
    }

    return Array.from(permissions);
  },
};

/**
 * Helper: Get current token
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Helper: Check if user has a specific role
 */
export function hasRole(role: string): boolean {
  const user = getStoredUser();
  return user?.roles.includes(role) || false;
}

/**
 * Helper: Get the current user's primary AdminRole (#1088).
 * Un compte Keycloak peut porter plusieurs rôles (user.roles est une liste
 * brute) — on retient le plus privilégié via AdminRoleLevel (0 = le plus
 * haut) pour l'affichage (badge de rôle dans AdminAppBar/Dashboard).
 * Retourne undefined si aucun rôle stocké ne correspond à un AdminRole
 * connu (cohérent avec `role &&` déjà utilisé aux call-sites existants).
 */
export function getCurrentAdminRole(): AdminRole | undefined {
  const user = getStoredUser();
  if (!user?.roles?.length) return undefined;

  let best: AdminRole | undefined;
  for (const role of user.roles) {
    const candidate = role.toUpperCase() as AdminRole;
    if (!(candidate in AdminRoleLevel)) continue;
    if (!best || AdminRoleLevel[candidate] < AdminRoleLevel[best]) {
      best = candidate;
    }
  }
  return best;
}

export default authProvider;
